import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseOAuthCallback, completeOAuthSignup } from '../services/oauthService';
import { setToken } from '../services/tokenService';
import { useAuth } from '../contexts/AuthContext';
import UsernameSelectionModal from './UsernameSelectionModal';

function OAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [suggestedUsername, setSuggestedUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const params = parseOAuthCallback(location.search);

      // Handle errors
      if (params.error) {
        console.log('OAuth callback error:', params.error, params.message);
        setError('Sign-in failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Handle successful login with token
      if (params.token) {
        setToken(params.token);
        // Pull the identity in before navigating: the provider only reads the
        // token on mount, and going home from here is a client-side route
        // change, not a fresh page load.
        await refreshUser();
        navigate('/', { replace: true });
        return;
      }

      // Handle username selection needed
      if (params.needsUsername && params.tempToken) {
        setTempToken(params.tempToken);
        setSuggestedUsername(params.suggestedUsername);
        setShowUsernameModal(true);
        return;
      }

      // If we get here, something went wrong
      setError('Invalid OAuth callback');
      setTimeout(() => navigate('/login'), 3000);
    };

    handleOAuthCallback();
  }, [location.search, navigate, refreshUser]);

  const handleUsernameSubmit = async (username) => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await completeOAuthSignup(tempToken, username);

      setToken(response.token);
      await refreshUser();

      setShowUsernameModal(false);
      navigate('/', { replace: true });
    } catch (err) {
      console.log('OAuth username submission error:', err);
      setError('Failed to create account. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      setShowUsernameModal(false);
      navigate('/login', { replace: true });
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-3">
        <div className="max-w-md w-full bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 text-center">
          <h3 className="text-yellow-800 text-lg font-bold mb-2">Authentication Error</h3>
          <p className="text-yellow-700 mb-4">{error}</p>
          <p className="text-yellow-600 text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-center min-h-screen p-3">
        <div className="max-w-md w-full bg-blue-50 border-2 border-dashed border-blue-200 rounded-lg p-6 text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <h3 className="text-blue-800 text-lg font-bold mb-2">Completing Sign In</h3>
          <p className="text-blue-600">Please wait...</p>
        </div>
      </div>

      <UsernameSelectionModal
        isOpen={showUsernameModal}
        onClose={handleModalClose}
        suggestedUsername={suggestedUsername}
        onSubmit={handleUsernameSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  );
}

export default OAuthCallback;